<template>
    <div>
        <div v-if="showPreview">
            <div class="import-dialog__error-title">File import có lỗi, vui lòng kiểm tra lại.</div>
            <TableErrorImportDepositRefund :response-error="listErrors"/>
        </div>
        <div v-else class="import-dialog">
            <div class="import-dialog__steps">
                <div class="import-dialog__step">
                    <label>Bước 1: Export danh sách hoặc tải file mẫu</label>
                    <button class="btn btn-primary import-dialog__download" type="button"
                            @click="downloadFileExcelExample">
                        <i class="el-icon-bottom"></i> Tải xuống
                    </button>
                </div>
                <div class="import-dialog__step">
                    <label>Bước 2: Sửa cột Mã trạng thái rồi tải file excel lên hệ thống</label>
                    <div v-if="fileImport" class="import-dialog__file">
                        <span>{{ fileImport.name }}</span>
                        <el-button type="text" @click="resetFile">
                            <i class="el-icon-delete" style="color: #f56c6c"></i>
                        </el-button>
                    </div>
                    <label v-else class="btn btn-warning import-dialog__upload" for="upload-deposit-refund">
                        <input
                            id="upload-deposit-refund"
                            accept=".xlsx,.xls"
                            style="display: none"
                            type="file"
                            @change="handleFileUpload"
                        />
                        <i class="el-icon-top"></i> Tải lên
                    </label>
                </div>
            </div>
        </div>
        <div class="import-dialog__footer">
            <button class="btn btn-secondary-muted" type="button" @click="isBack ? backToImport() : closeDialog()">
                {{ isBack ? "Quay lại" : "Hủy bỏ" }}
            </button>
            <el-button
                v-loading="disableImport"
                :disabled="disableImport || hasRowErrors"
                class="btn btn-primary text-white"
                style="background: #5d04b0; border: none"
                type="button"
                @click="checkImport ? handleImportFileExcel() : handlePreviewImportFileExcel()"
            >
                {{ checkImport ? "Import" : "Xác nhận" }}
            </el-button>
        </div>
    </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
    IMPORT_DEPOSIT_REFUND,
    PREVIEW_IMPORT_DEPOSIT_REFUND,
} from "@/core/services/store/deposit-refund/depositRefund.module";
import {ImportDepositRefundModel} from "@/view/pages/payments/deposit-refund/model/ImportDepositRefundModel";
import {STATUS_OPTIONS} from "@/view/pages/payments/deposit-refund/model/DepositRefundModel";
import TableErrorImportDepositRefund
    from "@/view/pages/payments/deposit-refund/component/TableErrorImportDepositRefund.vue";

const COLUMN_MAPPING = {
    Id: "id",
    "Mã HĐ": "contract_code",
    "Trạng thái": "processing_status_label",
    "Mã trạng thái": "processing_status",
};

const STATUS_MAP = Object.fromEntries(
    STATUS_OPTIONS.map((item) => [item.label.toLowerCase(), item.value])
);

export default {
    name: "DialogImportDepositRefund",
    components: {TableErrorImportDepositRefund},
    data() {
        return {
            fileImport: null,
            dataImport: [],
            listErrors: [],
            disableImport: false,
            checkImport: false,
            isBack: false,
        };
    },
    computed: {
        showPreview() {
            return this.listErrors.length > 0;
        },
        hasRowErrors() {
            return this.listErrors.some((item) => item.errors && item.errors.length > 0);
        },
    },
    methods: {
        downloadFileExcelExample() {
            const importHeaders = ["Id", "Mã HĐ", "Trạng thái", "Mã trạng thái"];
            const importSheet = XLSX.utils.aoa_to_sheet([importHeaders]);

            const statusHeaders = ["Mã trạng thái", "Trạng thái"];
            const statusRows = STATUS_OPTIONS.map((item) => [item.value, item.label]);
            const statusSheet = XLSX.utils.aoa_to_sheet([statusHeaders, ...statusRows]);

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, importSheet, "Import");
            XLSX.utils.book_append_sheet(workbook, statusSheet, "Danh sách trạng thái");
            XLSX.writeFile(workbook, "File mẫu import phí bổ trợ.xlsx");
        },
        handleFileUpload(event) {
            const file = event.target.files?.[0];
            if (file) {
                this.fileImport = file;
                this.readExcelFile(file);
            }
            event.target.value = "";
        },
        readExcelFile(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, {type: "array", cellDates: true});
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    raw: false,
                    dateNF: "dd/mm/yyyy",
                });
                const headers = (jsonData[0] || []).map((h) => (h ?? "").toString().trim());
                const rows = jsonData.slice(1);
                const columnDefs = headers
                    .map((header, colIndex) => ({header, colIndex, field: COLUMN_MAPPING[header]}))
                    .filter((col) => col.header && col.field);

                this.dataImport = rows
                    .filter(
                        (row) =>
                            Array.isArray(row) &&
                            row.some((cell) => cell !== null && cell !== undefined && cell !== "")
                    )
                    .map((row, index) => {
                        const obj = {row_index: index + 2};
                        columnDefs.forEach(({field, colIndex}) => {
                            obj[field] = this.normalizeCell(field, row[colIndex]);
                        });
                        return obj;
                    });
                this.checkImport = false;
                this.listErrors = [];
                this.isBack = false;
            };
            reader.readAsArrayBuffer(file);
        },
        normalizeCell(field, value) {
            if (value === null || value === undefined || value === "") return null;
            const text = value.toString().trim();
            if (!text) return null;

            if (field === "processing_status") {
                const num = Number(text);
                if (Number.isInteger(num) && num >= 1 && num <= 5) return num;
                const mapped = STATUS_MAP[text.toLowerCase()];
                return mapped !== undefined ? mapped : text;
            }
            if (field === "id") {
                const num = Number(String(text).replace(/\D/g, ""));
                return Number.isFinite(num) && num > 0 ? num : text;
            }
            return text;
        },
        collectItemErrors(item) {
            if (!item) return [];
            if (Array.isArray(item.error)) return item.error.filter(Boolean);
            if (typeof item.error === "string" && item.error) return [item.error];
            if (Array.isArray(item.errors)) return item.errors.filter(Boolean);
            if (item.errors && typeof item.errors === "object") {
                return Object.values(item.errors).flat().filter(Boolean);
            }
            if (item.message) return [item.message];
            return [];
        },
        unwrapPreviewPayload(res) {
            const root = res?.data !== undefined && !Array.isArray(res?.data) ? res.data : res;
            const rows = root?.dataImport ?? root?.data ?? (Array.isArray(res?.data) ? res.data : []);
            const checkImport = root?.checkImport ?? res?.checkImport ?? root?.isError !== true;
            return {
                rows: Array.isArray(rows) ? rows : [],
                checkImport: checkImport !== false && checkImport !== 0,
            };
        },
        mapPreviewRows(rows) {
            return rows.map((item, i) => {
                const errors = this.collectItemErrors(item);
                return new ImportDepositRefundModel(
                    item.row_index ?? item.row ?? i + 1,
                    {
                        deposit_refund_id: item.deposit_refund_id ?? item.id,
                        contract_code: item.contract_code,
                        processing_status: item.processing_status,
                    },
                    errors
                );
            });
        },
        resetFile() {
            this.fileImport = null;
            this.dataImport = [];
            this.checkImport = false;
            this.listErrors = [];
            this.isBack = false;
        },
        closeDialog() {
            this.$emit("closeDialog");
            this.$emit("setTitleError", "Import đề xuất hoàn phí bổ trợ cấp tốc");
            this.resetFile();
            this.disableImport = false;
        },
        backToImport() {
            this.resetFile();
            this.disableImport = false;
            this.$emit("setTitleError", "Import đề xuất hoàn phí bổ trợ cấp tốc");
        },
        async handlePreviewImportFileExcel() {
            this.disableImport = true;

            if (!this.fileImport) {
                this.$notify({title: "Thất bại", message: "Chưa có file nào được chọn", type: "error"});
                this.disableImport = false;
                return;
            }
            if (!this.dataImport.length) {
                this.$message.warning("Bạn không thể import khi file excel không có bản ghi nào");
                this.disableImport = false;
                return;
            }

            try {
                const res = await this.$store.dispatch(PREVIEW_IMPORT_DEPOSIT_REFUND, this.dataImport);
                const {rows, checkImport} = this.unwrapPreviewPayload(res);

                this.dataImport = rows.length ? rows : this.dataImport;
                this.checkImport = checkImport;
                this.listErrors = this.mapPreviewRows(rows.length ? rows : this.dataImport);
                this.isBack = true;

                if (this.hasRowErrors || !checkImport) {
                    this.$emit("setTitleError", "Báo lỗi");
                } else {
                    this.$message.success("Kiểm tra dữ liệu thành công, bấm Import để hoàn tất");
                }
            } catch (e) {
                const body = e?.data ?? e;
                const {rows} = this.unwrapPreviewPayload(body);
                if (rows.length) {
                    this.listErrors = this.mapPreviewRows(rows);
                    this.isBack = true;
                    this.$emit("setTitleError", "Báo lỗi");
                } else {
                    this.$message.error(body?.message || "Kiểm tra dữ liệu thất bại");
                }
            } finally {
                this.disableImport = false;
            }
        },
        async handleImportFileExcel() {
            this.disableImport = true;
            try {
                await this.$store.dispatch(IMPORT_DEPOSIT_REFUND, this.dataImport);
                this.$message.success("Import thành công");
                this.$emit("reload");
                this.closeDialog();
            } catch (e) {
                this.$message.error(e?.data?.message || e?.message || "Import thất bại");
            } finally {
                this.disableImport = false;
            }
        },
    },
};
</script>

<style scoped>
.import-dialog {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.import-dialog__steps {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.import-dialog__step {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.import-dialog__step label {
    font-weight: 500;
    margin: 0;
}

.import-dialog__upload,
.import-dialog__download {
    width: 130px;
    margin: 0;
    cursor: pointer;
}

.import-dialog__file {
    display: flex;
    align-items: center;
    gap: 8px;
}

.import-dialog__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
}

.btn-secondary-muted {
    background: #cacaca;
    border: none;
    color: #333;
}

.import-dialog__error-title {
    font-weight: 600;
    color: #d52323;
    margin-bottom: 8px;
}
</style>
